import { CheckCircle2, GitBranch } from "lucide-react";
import { useGitHubStatus, useRequestGitHubAccess } from "@/hooks/queries/use-github";
import { ApiError } from "@/lib/api/client";
import { GITHUB_RETURN_TO_KEY } from "@/lib/github-return-to";
import { Button } from "@/components/ui/button";

const DEFAULT_REASON = "so CompBox can list your repos, open pull requests and deploy";

function rememberReturnTo(returnTo: string) {
  try {
    sessionStorage.setItem(GITHUB_RETURN_TO_KEY, returnTo);
  } catch {
    // Storage blocked: the signed install state still carries returnTo.
  }
}

function requestErrorText(error: unknown): string {
  if (error instanceof ApiError && error.response.status === 429) return "Already requested, try again later.";
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/**
 * The one "Connect GitHub" step shown wherever something needs GitHub. An
 * organization admin installs the CompBox app and comes back to `returnTo`;
 * anyone else asks the admins to do it.
 */
export function ConnectGitHubGate({
  returnTo,
  teamId,
  reason = DEFAULT_REASON,
}: {
  returnTo: string;
  teamId?: string;
  reason?: string;
}) {
  const { data: status, isLoading } = useGitHubStatus(returnTo);
  const request = useRequestGitHubAccess();

  if (isLoading || !status) return <div className="h-10 animate-pulse rounded-lg bg-muted" />;

  if (!status.configured) {
    return <p className="text-sm text-muted-foreground">Connecting GitHub isn't available on this CompBox server yet.</p>;
  }

  if (status.canManage && status.installUrl) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Connect GitHub {reason}.</p>
        <Button asChild size="sm" className="shrink-0">
          <a href={status.installUrl} onClick={() => { rememberReturnTo(returnTo); }}>
            <GitBranch className="mr-2 h-4 w-4" />
            Connect GitHub
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-white/[0.08] p-3">
      <p className="text-sm text-muted-foreground">
        An organization admin needs to connect GitHub {reason}.
      </p>
      {teamId ? (
        request.isSuccess ? (
          <p className="flex items-center gap-1 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> Request sent
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={request.isPending}
              onClick={() => { request.mutate({ teamId, returnTo }); }}
            >
              Request access
            </Button>
            {request.isError ? <span className="text-xs text-destructive">{requestErrorText(request.error)}</span> : null}
          </div>
        )
      ) : null}
    </div>
  );
}
